import React, { useEffect, useRef } from 'react';
import { Animated, Easing, Text, TouchableOpacity } from 'react-native';
import { BORDER_RADIUS_TINY, FONTS, shadow } from '@/styles';
import { createStyles } from '@/contexts/theme-context';
import {
  EXPANDED_QUANTITY_WIDTH,
  QUANTITY_INPUT_WIDTH,
  QuantitySelector,
  Props as QuantitySelectorProps,
  TEXT_INPUT_CONTAINER_SPACING,
} from './quantity-selector';

const HIT_SLOP = {
  bottom: 8,
  left: 8,
  right: 8,
  top: 8,
};

// Add additional container spacing to account for
// both horizontal padding around TextInput
const TOTAL_EXPANDED_WIDTH =
  EXPANDED_QUANTITY_WIDTH + TEXT_INPUT_CONTAINER_SPACING;

export interface Props extends QuantitySelectorProps {
  onSelect: () => void;
  resting: boolean;
}

export const RestingQuantitySelector: React.FC<Props> = ({
  onSelect,
  resting,
  ...quantitySelectorProps
}) => {
  const [styles] = useStyles();
  const { quantity } = quantitySelectorProps;

  const animatedValueRef = useRef(new Animated.Value(resting ? 0 : 1));

  useEffect(() => {
    Animated.timing(animatedValueRef.current, {
      duration: 250,
      easing: Easing.out(Easing.exp),
      toValue: resting ? 0 : 1,
      useNativeDriver: false,
    }).start();
  }, [resting]);

  return (
    <Animated.View
      style={[
        {
          marginRight: animatedValueRef.current.interpolate({
            inputRange: [0, 1],
            outputRange: [8, 0],
          }),
          width: animatedValueRef.current.interpolate({
            inputRange: [0, 1],
            outputRange: [QUANTITY_INPUT_WIDTH, TOTAL_EXPANDED_WIDTH],
          }),
        },
      ]}
    >
      {resting ? (
        <TouchableOpacity
          hitSlop={HIT_SLOP}
          onPress={onSelect}
          style={styles.restingContainer}
        >
          <Text style={FONTS.primaryBold}>{quantity}</Text>
        </TouchableOpacity>
      ) : (
        // Intercept taps over text input when quantity selector is active
        <TouchableOpacity activeOpacity={1} style={styles.quantityContainer}>
          <QuantitySelector
            {...quantitySelectorProps}
            textInputStyle={styles.quantityTextInput}
          />
        </TouchableOpacity>
      )}
    </Animated.View>
  );
};

const useStyles = createStyles((theme) => ({
  quantityContainer: {
    ...shadow({
      color: theme.sk.color.border.primary,
      elevation: 4,
      opacity: 0.5,
      radius: TOTAL_EXPANDED_WIDTH,
      x: 0,
      y: 4,
    }),
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.divider.default,
    borderRadius: TOTAL_EXPANDED_WIDTH,
    borderWidth: 1,
    padding: 4,
  },
  quantityTextInput: {
    borderWidth: 0,
    marginHorizontal: TEXT_INPUT_CONTAINER_SPACING / 2,
  },
  restingContainer: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: BORDER_RADIUS_TINY,
    borderWidth: 1,
    height: QUANTITY_INPUT_WIDTH,
    justifyContent: 'center',
    width: QUANTITY_INPUT_WIDTH,
  },
}));
